/*
 * Generates the table of problems from data retrieved from the API.
 */

import React from "react";
import { Problem } from "../pages/ProblemsPage";
import { dateDisplay, companyDisplay, agentDisplay, DATE_FORMAT } from "../helpers/dataHelper";
import moment from "moment-timezone";
import MUIDataTable from "mui-datatables";
import clsx from "clsx";
import { Typography } from "@material-ui/core";
import useRowStyles from "../hooks/useRowStyles";

interface ProblemsTableProps {
    problems: Problem[];
    paging: {
        page: number;
        perPage: number;
        total: number;
        setPage: (page: number) => void;
        setRowsPerPage: (numberOfRows: number) => void;
    };
    sorting: {
        sortProperty: string;
        sortDirection: number;
        setSort: (property: string) => void;
    };
}

const ProblemsTable: React.FC<ProblemsTableProps> = ({ problems, paging, sorting }) => {
    const rowClasses = useRowStyles();
    const columns = [
        {
            label: "Problem ID",
            name: "id",
            options: {
                customHeadLabelRender: (columnMeta: any) => {
                    return (
                        <Typography component={"span"} noWrap={true} variant="body2">
                            {columnMeta.label}
                        </Typography>
                    );
                }
            }
        },
        {
            label: "Company Name",
            name: "client",
            options: {
                customBodyRenderLite: (dataIndex: number) => {
                    const problem = problems[dataIndex];
                    return companyDisplay(problem.client);
                }
            }
        },
        {
            label: "Subject",
            name: "subject"
        },
        {
            label: "Status",
            name: "status",
            options: {
                customBodyRender: (value: any) => {
                    return (
                        <Typography component={"span"} noWrap={true} variant="body2">
                            {value}
                        </Typography>
                    );
                }
            }
        },
        {
            label: "Priority",
            name: "priority",
            sortProperty: "priority"
        },
        {
            label: "Agent",
            name: "agent",
            options: {
                customBodyRender: (value: any) => {
                    return (
                        <Typography component={"span"} noWrap={true} variant="body2">
                            {agentDisplay(value)}
                        </Typography>
                    );
                }
            }
        },
        {
            label: "Last Modified",
            name: "lastModifiedAt",
            options: { customBodyRender: (value: any) => dateDisplay(value) }
        },
        {
            label: "URL",
            name: "url",
            options: {
                display: false
            }
        }
    ];

    return (
        <MUIDataTable
            title=""
            data={problems}
            columns={columns}
            options={{
                sortOrder: {
                    name: sorting.sortProperty,
                    direction: sorting.sortDirection === -1 ? "desc" : "asc"
                },
                viewColumns: false,
                print: false,
                filter: false,
                search: false,
                selectableRows: "none",
                serverSide: true,
                page: paging.page,
                rowsPerPage: paging.perPage ?? 30,
                count: paging.total,
                download: false,
                onColumnSortChange: (changedColumn: string) => {
                    sorting.setSort(changedColumn);
                },
                onChangeRowsPerPage: (numberOfRows: number) => {
                    paging.setRowsPerPage(numberOfRows);
                },
                onChangePage: (currentPage: number) => {
                    paging.setPage(currentPage);
                },
                setTableProps: () => {
                    return {
                        size: "small"
                    };
                },
                setRowProps: (row: any[]) => {
                    const overdueStatus = () => {
                        const timeToDueDate = moment.utc(row[9], DATE_FORMAT).diff(moment.utc());
                        if (timeToDueDate < 0) {
                            return "overdue";
                        } else if (timeToDueDate < 7200000) {
                            // 2 hours
                            return "close";
                        }

                        return "ok";
                    };

                    return {
                        className: clsx(
                            overdueStatus() === "overdue" && rowClasses.overdue,
                            overdueStatus() === "close" && rowClasses.close,
                            overdueStatus() === "ok" && rowClasses.standard
                        )
                    };
                },
                onRowClick: (rowData: string[]) => {
                    window.open(`${rowData[7]}`);
                },
                rowsPerPageOptions: [30, 100, 300]
            }}
        />
    );
};

export default ProblemsTable;
